import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Shield, Users, Calendar, Bell, TrendingUp, Phone, Mail, Trash2, LogOut, Edit2, FileText, Settings, Send, Gift, Plus, Copy, ToggleLeft, ToggleRight, Activity, Clock, User, Building2 } from 'lucide-react';
import EditPermitModal from '../components/EditPermitModal';
import AccountSettings from '../components/AccountSettings';
import './AdminDashboard.css';

const AdminDashboard = ({ setIsAuthenticated }) => {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('overview'); // overview, clients-only, all-permits, expired-permits, clients, promo-codes, activity, expired-trials, duplicate-permits, archived-accounts
  const [clientFilter, setClientFilter] = useState('all'); // all, individual, company
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientPermits, setClientPermits] = useState([]);
  const [duplicatePermits, setDuplicatePermits] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);
  const [mergingPermit, setMergingPermit] = useState(false);
  const [archivedAccounts, setArchivedAccounts] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [loadingPermits, setLoadingPermits] = useState(false);
  const [editingPermit, setEditingPermit] = useState(null);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [allPermits, setAllPermits] = useState([]);
  const [expiredPermits, setExpiredPermits] = useState([]);
  const [overdueQualifications, setOverdueQualifications] = useState([]);
  const [syncingQualifications, setSyncingQualifications] = useState(false);
  const [showEmployeeLimitModal, setShowEmployeeLimitModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [newEmployeeLimit, setNewEmployeeLimit] = useState(50);
  const [showMarkEmployeeModal, setShowMarkEmployeeModal] = useState(false);
  const [markingAsEmployee, setMarkingAsEmployee] = useState(null);
  const [selectedCompanyForEmployee, setSelectedCompanyForEmployee] = useState('');
  // Company employees view state
  const [selectedCompanyForView, setSelectedCompanyForView] = useState(null);
  const [companyEmployees, setCompanyEmployees] = useState([]);
  const [loadingCompanyEmployees, setLoadingCompanyEmployees] = useState(false);
  const [selectedEmployeeForPermits, setSelectedEmployeeForPermits] = useState(null);
  // Overdue qualifications selection
  const [selectedQualifications, setSelectedQualifications] = useState([]);
  // Edit Company Details modal state
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [editingCompanyDetails, setEditingCompanyDetails] = useState(null);
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editAdminName, setEditAdminName] = useState('');
  // Add Client modal state
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [addClientForm, setAddClientForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    account_type: 'individual',
    company_name: '',
    send_welcome_email: true
  });
  const [addingClient, setAddingClient] = useState(false);
  // Fix Subscription modal state
  const [showFixSubscriptionModal, setShowFixSubscriptionModal] = useState(false);
  const [fixingSubscriptionUser, setFixingSubscriptionUser] = useState(null);
  const [fixingSubscription, setFixingSubscription] = useState(false);
  const [newPromoCode, setNewPromoCode] = useState({
    code: '',
    description: 'Free account promo code',
    valid_for_days: 365,
    max_uses: null,
    account_types: ['individual', 'company']
  });

  useEffect(() => {
    fetchData();
    fetchOverdueQualifications();
    fetchAdminUser();
  }, []);

  // Handle browser back button - always go to home page
  useEffect(() => {
    // Push a new state so we can detect when user tries to go back
    window.history.pushState({ page: 'admin' }, '', window.location.pathname);
    
    const handlePopState = (event) => {
      // When back button is pressed, redirect to home page
      window.location.href = '/';
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Get company accounts for dropdown
  const companyAccounts = clients.filter(c => c.account_type === 'company');

  // Filter clients based on account type
  const filteredClients = clients.filter(client => {
    if (clientFilter === 'all') return true;
    return client.account_type === clientFilter;
  });

  const fetchAdminUser = async () => {
    try {
      const response = await axiosInstance.get('/auth/me');
      setAdminUser(response.data);
    } catch (error) {
      console.error('Failed to fetch admin user');
    }
  };

  const fetchOverdueQualifications = async () => {
    try {
      const response = await axiosInstance.get('/admin/overdue-qualifications');
      setOverdueQualifications(response.data);
    } catch (error) {
      console.error('Failed to fetch overdue qualifications:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, clientsRes] = await Promise.all([
        axiosInstance.get('/admin/stats'),
        axiosInstance.get('/admin/clients'),
      ]);

      setStats(statsRes.data);
      setClients(clientsRes.data);
      setSelectedUsers([]);
      
      // Prepare all permits list with client info
      const allPermitsList = [];
      const expiredPermitsList = [];
      const now = new Date();
      
      clientsRes.data.forEach(client => {
        client.permits.forEach(permit => {
          const permitWithClient = {
            ...permit,
            client_name: client.full_name,
            client_email: client.email,
            client_phone: client.phone,
            subscription_status: client.subscription_status
          };
          allPermitsList.push(permitWithClient);
          
          // Check if expired
          const expDate = new Date(permit.expiration_date);
          if (expDate < now) {
            expiredPermitsList.push(permitWithClient);
          }
        });
      });
      
      setAllPermits(allPermitsList);
      setExpiredPermits(expiredPermitsList);
      
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLog = async () => {
    setLoadingActivity(true);
    try {
      const response = await axiosInstance.get('/admin/activity?limit=100');
      setActivityLog(response.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch activity log:', error);
      toast.error('Failed to load activity log');
    } finally {
      setLoadingActivity(false);
    }
  };

  const fetchDuplicatePermits = async () => {
    setLoadingDuplicates(true);
    try {
      const response = await axiosInstance.get('/admin/duplicate-permits');
      setDuplicatePermits(response.data.duplicates || []);
    } catch (error) {
      console.error('Failed to fetch duplicate permits:', error);
      toast.error('Failed to load duplicate permits');
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const fetchArchivedAccounts = async () => {
    setLoadingArchived(true);
    try {
      const response = await axiosInstance.get('/admin/archived-accounts');
      setArchivedAccounts(response.data.archived_accounts || []);
    } catch (error) {
      console.error('Failed to fetch archived accounts:', error);
      toast.error('Failed to load archived accounts');
    } finally {
      setLoadingArchived(false);
    }
  };

  const handleMergePermits = async (sourcePermitId, targetPermitId) => {
    if (!window.confirm('Are you sure you want to merge these permits? The source permit will be deleted.')) {
      return;
    }
    
    setMergingPermit(true);
    try {
      await axiosInstance.post('/admin/merge-permits', {
        source_permit_id: sourcePermitId,
        target_permit_id: targetPermitId,
        merge_qualifications: true
      });
      toast.success('Permits merged successfully');
      setSelectedDuplicate(null);
      fetchDuplicatePermits();
      fetchClients(); // Refresh client data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge permits');
    } finally {
      setMergingPermit(false);
    }
  };

  const handleRestoreAccount = async (archiveId) => {
    if (!window.confirm('Are you sure you want to restore this account?')) {
      return;
    }
    
    try {
      await axiosInstance.post(`/admin/restore-account/${archiveId}`);
      toast.success('Account restored successfully');
      fetchArchivedAccounts();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore account');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getPermitTypeName = (type, customName) => {
    if (type === 'other' && customName) {
      return customName;
    }
    const names = {
      // BSIS Permits
      firearms: 'BSIS Exposed Firearm Permit',
      guard_card: 'BSIS Guard Card',
      baton: 'BSIS Baton Permit',
      bsis_ppo: 'BSIS PPO License',
      bsis_qm: 'BSIS QM License',
      bsis_pi: 'BSIS Private Investigator License',
      bsis_locksmith: 'BSIS Locksmith License',
      bsis_alarm: 'BSIS Alarm Company License',
      // CCW Permits
      ccw_ca: 'California CCW Permit',
      ccw_out_of_state: 'Out of State CCW Permit',
      hr218: 'HR218 LEOSA Permit',
      // Professional Permits
      doj_coe: 'DOJ COE License (Renews Yearly)',
      doj_firearms_instructor: 'CA DOJ Firearms Instructor License',
      doj_ccw_instructor: 'CA DOJ CCW Instructor License',
      first_aid: 'First Aid License',
      cpr: 'CPR License',
      tecc: 'TECC License',
      axon_taser_instructor: 'AXON TASER Instructor License',
      sabre_oc_instructor: 'SABRE OC Instructor License',
      nra_instructor: 'NRA Instructor License',
      uscca_instructor: 'USCCA Instructor License',
      crpa_instructor: 'CRPA Instructor License',
      // Personal Permits
      drivers_license: 'Drivers License',
      passport: 'Passport',
      vehicle_insurance: 'Vehicle Insurance',
      // Legacy support
      ccw: 'CCW Permit',
      private_investigator: 'Private Investigator License',
      locksmith: 'Locksmith License',
      alarm_installer: 'Alarm Operator/Installer',
      // Custom
      other: customName || 'Custom Permit',
    };
    return names[type] || type;
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === clients.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(clients.map((client) => client.user_id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users to delete');
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedUsers.length} user(s)? This will also delete all their permits and data. This action cannot be undone.`
    );

    if (!confirmDelete) return;

    setDeleting(true);
    try {
      const response = await axiosInstance.delete('/admin/users', {
        data: { user_ids: selectedUsers },
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete users');
    } finally {
      setDeleting(false);
    }
  };

  // Sync qualifications - fixes overdue status for completed quals
  const handleSyncQualifications = async () => {
    setSyncingQualifications(true);
    try {
      // First sync from permit data
      const syncResult = await axiosInstance.post('/admin/sync-qualifications');
      
      // Then cleanup overdue that are actually completed
      const cleanupResult = await axiosInstance.post('/admin/cleanup-overdue-qualifications');
      
      toast.success(
        `Sync complete! Synced: ${syncResult.data.synced_count}, Cleaned: ${cleanupResult.data.cleaned_count}`
      );
      
      // Refresh the overdue qualifications list
      fetchOverdueQualifications();
    } catch (error) {
      toast.error('Failed to sync qualifications');
      console.error(error);
    } finally {
      setSyncingQualifications(false);
    }
  };

  // Handle qualification selection
  const handleSelectQualification = (qualId) => {
    setSelectedQualifications(prev => 
      prev.includes(qualId) 
        ? prev.filter(id => id !== qualId)
        : [...prev, qualId]
    );
  };

  const handleSelectAllQualifications = () => {
    if (selectedQualifications.length === overdueQualifications.length) {
      setSelectedQualifications([]);
    } else {
      setSelectedQualifications(overdueQualifications.map(q => q.qualification_id));
    }
  };

  // Mark selected qualifications as complete
  const handleMarkComplete = async () => {
    if (selectedQualifications.length === 0) {
      toast.error('Please select qualifications to mark as complete');
      return;
    }
    
    const confirmMark = window.confirm(
      `Mark ${selectedQualifications.length} qualification(s) as complete? This will remove them from the overdue list.`
    );
    
    if (!confirmMark) return;
    
    try {
      await axiosInstance.post('/admin/mark-qualifications-complete', {
        qualification_ids: selectedQualifications
      });
      toast.success(`Marked ${selectedQualifications.length} qualification(s) as complete`);
      setSelectedQualifications([]);
      fetchOverdueQualifications();
    } catch (error) {
      toast.error('Failed to mark qualifications as complete');
    }
  };

  // Delete selected qualifications
  const handleDeleteQualifications = async () => {
    if (selectedQualifications.length === 0) {
      toast.error('Please select qualifications to delete');
      return;
    }
    
    const confirmDelete = window.confirm(
      `DELETE ${selectedQualifications.length} qualification record(s)? This will permanently remove them from the system. Only do this for outdated/duplicate records.`
    );
    
    if (!confirmDelete) return;
    
    try {
      await axiosInstance.delete('/admin/qualifications', {
        data: { qualification_ids: selectedQualifications }
      });
      toast.success(`Deleted ${selectedQualifications.length} qualification record(s)`);
      setSelectedQualifications([]);
      fetchOverdueQualifications();
    } catch (error) {
      toast.error('Failed to delete qualifications');
    }
  };

  const handleViewPermits = async (client) => {
    setSelectedClient(client);
    setLoadingPermits(true);
    setView('permits');
    
    try {
      const response = await axiosInstance.get(`/admin/users/${client.user_id}/permits`);
      setClientPermits(response.data);
    } catch (error) {
      toast.error('Failed to load permits');
      setClientPermits([]);
    } finally {
      setLoadingPermits(false);
    }
  };

  // View company employees (for company accounts)
  const handleViewCompanyEmployees = async (company) => {
    setSelectedCompanyForView(company);
    setLoadingCompanyEmployees(true);
    setView('company-employees');
    setSelectedEmployeeForPermits(null);
    
    try {
      const response = await axiosInstance.get(`/admin/users/${company.user_id}/permits`);
      const permits = response.data;
      
      // Extract unique employees from permits (by employee_name)
      const employeeMap = new Map();
      permits.forEach(permit => {
        const empName = permit.employee_name?.trim();
        if (empName) {
          const key = empName.toLowerCase();
          if (!employeeMap.has(key)) {
            employeeMap.set(key, {
              name: empName,
              permits: [],
              permitCount: 0,
              employee_status: permit.employee_status || 'active'
            });
          }
          employeeMap.get(key).permits.push(permit);
          employeeMap.get(key).permitCount++;
          // If any permit is terminated, mark employee as terminated
          if (permit.employee_status === 'terminated') {
            employeeMap.get(key).employee_status = 'terminated';
          }
        }
      });
      
      // Sort alphabetically by name
      const employees = Array.from(employeeMap.values()).sort((a, b) => 
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
      
      setCompanyEmployees(employees);
    } catch (error) {
      toast.error('Failed to load company employees');
      setCompanyEmployees([]);
    } finally {
      setLoadingCompanyEmployees(false);
    }
  };

  // View permits for a specific employee within a company
  const handleViewEmployeePermits = (employee) => {
    setSelectedEmployeeForPermits(employee);
  };

  const handleEditPermit = (permit) => {
    setEditingPermit(permit);
  };

  const handleDeletePermit = async (permitId, permitNumber, clientName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete permit ${permitNumber} for ${clientName}? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      await axiosInstance.delete(`/admin/permits/${permitId}`);
      toast.success(`Permit ${permitNumber} deleted successfully`);
      // Refresh permits for the selected client
      if (selectedClient) {
        const response = await axiosInstance.get(`/admin/users/${selectedClient.user_id}/permits`);
        setClientPermits(response.data);
      }
      // Also refresh main data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete permit');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${userName}? This will permanently delete all their permits and data. This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      await axiosInstance.delete('/admin/users', {
        data: { user_ids: [userId] },
      });
      toast.success(`${userName} deleted successfully`);
      fetchData();
      // If viewing this user's permits, go back to clients view
      if (selectedClient?.user_id === userId) {
        setSelectedClient(null);
        setClientPermits([]);
        setView('clients');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handlePermitUpdated = async () => {
    setEditingPermit(null);
    // Refresh permits for the selected client
    if (selectedClient) {
      try {
        const response = await axiosInstance.get(`/admin/users/${selectedClient.user_id}/permits`);
        setClientPermits(response.data);
      } catch (error) {
        console.error('Failed to refresh permits');
      }
    }
    // Also refresh main data
    fetchData();
  };

  const handleTestNotifications = async (client) => {
    const toastId = toast.loading(`Testing notifications for ${client.full_name}...`);
    try {
      const response = await axiosInstance.post(`/admin/test-notifications/${client.user_id}`);
      const results = response.data;
      
      let message = `📧 Email: ${results.email_test?.success ? '✅ Sent' : '❌ Failed'}\n`;
      message += `📱 SMS: ${results.sms_test?.success ? '✅ Sent' : '❌ Failed'}`;
      
      if (results.email_test?.success && results.sms_test?.success) {
        toast.success(`Test notifications sent to ${client.full_name}!\n${message}`, { id: toastId, duration: 5000 });
      } else {
        const errorDetails = [];
        if (!results.email_test?.success) errorDetails.push(`Email: ${results.email_test?.error || 'Unknown error'}`);
        if (!results.sms_test?.success) errorDetails.push(`SMS: ${results.sms_test?.error || 'Unknown error'}`);
        toast.error(`Some notifications failed:\n${errorDetails.join('\n')}`, { id: toastId, duration: 8000 });
      }
    } catch (error) {
      toast.error(`Failed to test notifications: ${error.response?.data?.detail || error.message}`, { id: toastId });
    }
  };

  // Promo Code Functions
  const fetchPromoCodes = async () => {
    try {
      const response = await axiosInstance.get('/admin/promo-codes');
      setPromoCodes(response.data);
    } catch (error) {
      console.error('Failed to fetch promo codes:', error);
    }
  };

  const handleCreatePromoCode = async (e) => {
    e.preventDefault();
    if (!newPromoCode.code.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    
    try {
      const response = await axiosInstance.post('/admin/promo-codes', {
        ...newPromoCode,
        max_uses: newPromoCode.max_uses || null
      });
      toast.success(`Promo code "${response.data.code}" created!`);
      setShowPromoForm(false);
      setNewPromoCode({
        code: '',
        description: 'Free account promo code',
        valid_for_days: 365,
        max_uses: null,
        account_types: ['individual', 'company']
      });
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const handleTogglePromoCode = async (code) => {
    try {
      const response = await axiosInstance.put(`/admin/promo-codes/${code}/toggle`);
      toast.success(response.data.message);
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to toggle promo code');
    }
  };

  const handleDeletePromoCode = async (code) => {
    if (!window.confirm(`Are you sure you want to delete promo code "${code}"?`)) return;
    
    try {
      await axiosInstance.delete(`/admin/promo-codes/${code}`);
      toast.success('Promo code deleted');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  const handleEditEmployeeLimit = (client) => {
    setEditingCompany(client);
    setNewEmployeeLimit(client.employee_limit || 50);
    setShowEmployeeLimitModal(true);
  };

  const handleUpdateEmployeeLimit = async () => {
    if (!editingCompany) return;
    
    try {
      await axiosInstance.put(`/admin/company/${editingCompany.user_id}/employee-limit`, {
        employee_limit: parseInt(newEmployeeLimit)
      });
      toast.success(`Employee limit updated to ${newEmployeeLimit}`);
      setShowEmployeeLimitModal(false);
      setEditingCompany(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee limit');
    }
  };

  const handleEditCompanyDetails = (client) => {
    setEditingCompanyDetails(client);
    setEditCompanyName(client.company_name || '');
    setEditAdminName(client.full_name || '');
    setShowEditCompanyModal(true);
  };

  const handleUpdateCompanyDetails = async () => {
    if (!editingCompanyDetails) return;
    
    try {
      await axiosInstance.put(`/admin/company/${editingCompanyDetails.user_id}/details`, {
        company_name: editCompanyName,
        full_name: editAdminName
      });
      toast.success('Company details updated successfully');
      setShowEditCompanyModal(false);
      setEditingCompanyDetails(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company details');
    }
  };

  const handleOpenMarkAsEmployee = (client) => {
    setMarkingAsEmployee(client);
    setSelectedCompanyForEmployee('');
    setShowMarkEmployeeModal(true);
  };

  const handleMarkAsEmployee = async () => {
    if (!markingAsEmployee || !selectedCompanyForEmployee) {
      toast.error('Please select a company');
      return;
    }
    
    try {
      await axiosInstance.put(`/admin/user/${markingAsEmployee.user_id}/employee-status`, {
        is_company_employee: true,
        company_id: selectedCompanyForEmployee
      });
      toast.success(`${markingAsEmployee.full_name} marked as company employee`);
      setShowMarkEmployeeModal(false);
      setMarkingAsEmployee(null);
      fetchData();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as employee');
    }
  };

  const handleMergeIntoCompany = async () => {
    if (!markingAsEmployee || !selectedCompanyForEmployee) {
      toast.error('Please select a company');
      return;
    }
    
    if (!window.confirm(`This will MERGE ${markingAsEmployee.full_name}'s permits into the company account and DELETE their individual account. Continue?`)) {
      return;
    }
    
    try {
      await axiosInstance.post('/admin/merge-user', {
        source_user_id: markingAsEmployee.user_id,
        target_user_id: selectedCompanyForEmployee
      });
      toast.success(`${markingAsEmployee.full_name}'s permits merged into company account`);
      setShowMarkEmployeeModal(false);
      setMarkingAsEmployee(null);
      fetchData();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge user');
    }
  };

  // Handle Add Client form submission
  const handleAddClient = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!addClientForm.full_name.trim()) {
      toast.error('Full name is required');
      return;
    }
    if (!addClientForm.email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!addClientForm.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    if (addClientForm.account_type === 'company' && !addClientForm.company_name.trim()) {
      toast.error('Company name is required for company accounts');
      return;
    }
    
    setAddingClient(true);
    try {
      const response = await axiosInstance.post('/admin/create-user', {
        full_name: addClientForm.full_name.trim(),
        email: addClientForm.email.trim().toLowerCase(),
        phone: addClientForm.phone.trim(),
        account_type: addClientForm.account_type,
        company_name: addClientForm.account_type === 'company' ? addClientForm.company_name.trim() : null,
        generate_password: true,
        send_welcome_email: addClientForm.send_welcome_email
      });
      
      toast.success(`Client account created for ${addClientForm.full_name}!`);
      
      // Show temporary password if email wasn't sent
      if (!addClientForm.send_welcome_email && response.data.temporary_password) {
        toast.info(`Temporary password: ${response.data.temporary_password}`, { duration: 10000 });
      }
      
      // Reset form and close modal
      setAddClientForm({
        full_name: '',
        email: '',
        phone: '',
        account_type: 'individual',
        company_name: '',
        send_welcome_email: true
      });
      setShowAddClientModal(false);
      
      // Refresh client list
      fetchData();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client account');
    } finally {
      setAddingClient(false);
    }
  };

  // Handle Fix Subscription
  const handleFixSubscription = async () => {
    if (!fixingSubscriptionUser) return;
    
    setFixingSubscription(true);
    try {
      await axiosInstance.put(`/admin/users/${fixingSubscriptionUser.user_id}/fix-subscription`, {
        subscription_status: 'active',
        days_to_add: 365
      });
      
      toast.success(`Subscription activated for ${fixingSubscriptionUser.full_name} (1 year)`);
      setShowFixSubscriptionModal(false);
      setFixingSubscriptionUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix subscription');
    } finally {
      setFixingSubscription(false);
    }
  };

  const copyPromoCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied "${code}" to clipboard`);
  };

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = 'STC-';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPromoCode({ ...newPromoCode, code });
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      <div className="admin-header">
        <img src="/stc-logo.png" alt="Southwest Training Center" className="admin-logo" />
        <div className="admin-header-text">
          <h1 className="admin-title">Admin Dashboard</h1>
          <p className="admin-subtitle">Southwest Training Center Permit Tracker</p>
        </div>
        <div className="admin-header-actions">
          <button 
            className="header-settings-btn" 
            onClick={() => setShowAccountSettings(true)}
            title="Account Settings"
          >
            <Settings size={20} />
          </button>
          <button className="header-logout-btn" onClick={handleLogout}>
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </div>

      <div className="admin-nav">
        <button
          className={`admin-nav-btn ${view === 'overview' ? 'active' : ''}`}
          onClick={() => setView('overview')}
        >
          <TrendingUp size={18} />
          Overview
        </button>
        <button
          className={`admin-nav-btn ${view === 'clients' ? 'active' : ''}`}
          onClick={() => { setView('clients'); setClientFilter('all'); }}
        >
          <Users size={18} />
          All Clients
        </button>
        <button
          className={`admin-nav-btn ${view === 'individual-clients' ? 'active' : ''}`}
          onClick={() => { setView('individual-clients'); setClientFilter('individual'); }}
        >
          👤 Individual
        </button>
        <button
          className={`admin-nav-btn ${view === 'company-clients' ? 'active' : ''}`}
          onClick={() => { setView('company-clients'); setClientFilter('company'); }}
        >
          🏢 Company
        </button>
        <button
          className={`admin-nav-btn ${view === 'promo-codes' ? 'active' : ''}`}
          onClick={() => { setView('promo-codes'); fetchPromoCodes(); }}
        >
          <Gift size={18} />
          Promo Codes
        </button>
        <button
          className={`admin-nav-btn ${view === 'activity' ? 'active' : ''}`}
          onClick={() => { setView('activity'); fetchActivityLog(); }}
        >
          <Activity size={18} />
          Activity
        </button>
        <button
          className={`admin-nav-btn expired-trials-btn ${view === 'expired-trials' ? 'active' : ''}`}
          onClick={() => setView('expired-trials')}
        >
          <Clock size={18} />
          Expired Trials
          {clients.filter(c => c.subscription_status === 'expired' && !c.promo_code_used && c.days_until_deletion !== undefined).length > 0 && (
            <span className="badge-count">{clients.filter(c => c.subscription_status === 'expired' && !c.promo_code_used && c.days_until_deletion !== undefined).length}</span>
          )}
        </button>
        <button
          className={`admin-nav-btn duplicate-permits-btn ${view === 'duplicate-permits' ? 'active' : ''}`}
          onClick={() => { setView('duplicate-permits'); fetchDuplicatePermits(); }}
        >
          <Copy size={18} />
          Duplicates
        </button>
        <button
          className={`admin-nav-btn archived-btn ${view === 'archived-accounts' ? 'active' : ''}`}
          onClick={() => { setView('archived-accounts'); fetchArchivedAccounts(); }}
        >
          <Trash2 size={18} />
          Archived
        </button>
      </div>

      {view === 'overview' && stats && (
        <div className="admin-overview">
          <div className="stats-grid admin-stats">
            <div 
              className="stat-card clickable-stat"
              onClick={() => setView('clients-only')}
              title="Click to view all clients"
            >
              <div className="stat-icon" style={{ backgroundColor: '#DBEAFE' }}>
                <Users size={24} style={{ color: '#1E40AF' }} />
              </div>
              <div className="stat-content">
                <p className="stat-label">Total Clients</p>
                <p className="stat-value">{stats.total_clients}</p>
              </div>
              <div className="stat-arrow">→</div>
            </div>

            <div 
              className="stat-card clickable-stat"
              onClick={() => setView('all-permits')}
              title="Click to view all permits"
            >
              <div className="stat-icon" style={{ backgroundColor: '#D1FAE5' }}>
                <Shield size={24} style={{ color: '#065F46' }} />
              </div>
              <div className="stat-content">
                <p className="stat-label">Total Permits</p>
                <p className="stat-value">{stats.total_permits}</p>
              </div>
              <div className="stat-arrow">→</div>
            </div>

            <div 
              className="stat-card clickable-stat expired-stat"
              onClick={() => setView('expired-permits')}
              title="Click to view expired permits"
            >
              <div className="stat-icon" style={{ backgroundColor: '#FEE2E2' }}>
                <Calendar size={24} style={{ color: '#991B1B' }} />
              </div>
              <div className="stat-content">
                <p className="stat-label">Expired Permits</p>
                <p className="stat-value">{stats.expired_permits}</p>
              </div>
              <div className="stat-arrow">→</div>
            </div>

            <div 
              className="stat-card clickable-stat"
              onClick={() => {
                // Scroll to upcoming qualifications section
                const upcomingSection = document.querySelector('.admin-upcoming');
                if (upcomingSection) {
                  upcomingSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              title="Click to view upcoming qualifications"
            >
              <div className="stat-icon" style={{ backgroundColor: '#FEF3C7' }}>
                <Bell size={24} style={{ color: '#92400E' }} />
              </div>
              <div className="stat-content">
                <p className="stat-label">Upcoming Quals</p>
                <p className="stat-value">{stats.upcoming_qualifications?.length || 0}</p>
              </div>
              <div className="stat-arrow">↓</div>
            </div>

            <div 
              className={`stat-card clickable-stat ${overdueQualifications.length > 0 ? 'overdue-stat' : ''}`}
              onClick={() => setView('overdue-qualifications')}
              title="Click to view overdue BSIS qualifications"
            >
              <div className="stat-icon" style={{ backgroundColor: overdueQualifications.length > 0 ? '#FEE2E2' : '#E2E8F0' }}>
                <Calendar size={24} style={{ color: overdueQualifications.length > 0 ? '#DC2626' : '#64748B' }} />
              </div>
              <div className="stat-content">
                <p className="stat-label">Overdue Quals</p>
                <p className="stat-value" style={{ color: overdueQualifications.length > 0 ? '#EF4444' : undefined }}>
                  {overdueQualifications.length}
                </p>
              </div>
              <div className="stat-arrow">→</div>
            </div>
          </div>

          {stats.upcoming_qualifications && stats.upcoming_qualifications.length > 0 && (
            <div className="upcoming-section admin-upcoming">
              <h2 className="section-title">Next Qualification Per Client</h2>
              <div className="upcoming-list">
                {[...stats.upcoming_qualifications]
                  .sort((a, b) => a.user_name.localeCompare(b.user_name))
                  .map((qual, idx) => (
                  <div 
                    key={idx} 
                    className="upcoming-item clickable"
                    onClick={() => handleViewPermits({
                      user_id: qual.user_id,
                      full_name: qual.user_name,
                      email: qual.user_email,
                      phone: qual.user_phone
                    })}
                    title={`Click to view ${qual.user_name}'s permits`}
                  >
                    <div className="upcoming-date">
                      <span className="date-day">{new Date(qual.due_date).getDate()}</span>
                      <span className="date-month">
                        {new Date(qual.due_date).toLocaleDateString('en-US', { month: 'short' })}
                      </span>
                    </div>
                    <div className="upcoming-info">
                      <p className="upcoming-client">
                        {qual.user_name}
                        <span className={`account-type-badge ${qual.account_type === 'company' ? 'company' : 'individual'}`}>
                          {qual.account_type === 'company' ? '🏢 Company' : '👤 Individual'}
                        </span>
                      </p>
                      {qual.account_type === 'company' && qual.company_name && (
                        <p className="upcoming-company">{qual.company_name}</p>
                      )}
                      <p className="upcoming-permit">
                        <strong>Permit:</strong> {qual.permit_number} 
                        <span className="permit-type-tag">
                          {getPermitTypeName(qual.permit_type)}
                        </span>
                      </p>
                      <p className="upcoming-qual-name">
                        <strong>Qualification:</strong> {qual.qualification_name}
                      </p>
                      <p className="upcoming-days">{qual.days_until} days until due</p>
                    </div>
                    <div className="upcoming-arrow">→</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TOTAL CLIENTS VIEW - Simple list with name, status, phone, email */}
      {view === 'clients-only' && (
        <div className="clients-section">
          <div className="clients-header">
            <h2 className="section-title">
              <Users size={24} />
              Total Clients ({filteredClients.length})
            </h2>
            <div className="header-actions">
              {/* Filter Buttons */}
              <div className="client-filter-buttons">
                <button
                  className={`filter-btn ${clientFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setClientFilter('all')}
                >
                  All ({clients.length})
                </button>
                <button
                  className={`filter-btn ${clientFilter === 'individual' ? 'active' : ''}`}
                  onClick={() => setClientFilter('individual')}
                >
                  👤 Individual ({clients.filter(c => c.account_type === 'individual').length})
                </button>
                <button
                  className={`filter-btn ${clientFilter === 'company' ? 'active' : ''}`}
                  onClick={() => setClientFilter('company')}
                >
                  🏢 Company ({clients.filter(c => c.account_type === 'company').length})
                </button>
              </div>
              <button className="btn-secondary" onClick={() => setView('overview')}>
                ← Back to Overview
              </button>
            </div>
          </div>
          <div className="clients-table-container">
            <table className="clients-table simple-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr key={client.user_id}>
                    <td>
                      <div 
                        className="client-name clickable"
                        onClick={() => handleViewPermits(client)}
                        title="Click to view permits"
                      >
                        {client.full_name}
                      </div>
                      <div className="client-account-type">
                        {client.account_type === 'company' ? '🏢 Company' : '👤 Individual'}
                      </div>
                    </td>
                    <td>
                      <div className="subscription-status-cell">
                        {client.promo_code_used ? (
                          <span className="subscription-badge subscription-promo" title={`Promo code: ${client.promo_code_used}`}>
                            🎁 PROMO ({client.promo_code_used})
                          </span>
                        ) : (
                          <>
                            <span className={`subscription-badge subscription-${client.subscription_status}`}>
                              {client.subscription_status === 'active' && '✓ PAID'}
                              {client.subscription_status === 'trial' && `🕐 TRIAL (${client.days_remaining}d)`}
                              {client.subscription_status === 'expired' && '✗ EXPIRED'}
                              {client.subscription_status === 'canceling' && '⚠ CANCELING'}
                            </span>
                            {client.subscription_status === 'expired' && (
                              <button
                                className="btn-fix-subscription"
                                onClick={() => {
                                  setFixingSubscriptionUser(client);
                                  setShowFixSubscriptionModal(true);
                                }}
                                title="Manually activate subscription after confirming payment"
                              >
                                🔧 Fix
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="contact-item">
                        <Phone size={14} />
                        {client.phone}
                      </span>
                    </td>
                    <td>
                      <span className="contact-item">
                        <Mail size={14} />
                        {client.email}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon-edit"
                          onClick={() => handleViewPermits(client)}
                          title="View and manage this client's permits"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="btn-icon-test"
                          onClick={() => handleTestNotifications(client)}
                          title="Send a test email and SMS to verify delivery"
                        >
                          <Send size={16} />
                        </button>
                        <button
                          className="btn-icon-danger"
                          onClick={() => {
                            setSelectedUsers([client.user_id]);
                            setTimeout(() => handleDeleteSelected(), 100);
                          }}
                          title="Permanently delete this client and all their permits"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ALL PERMITS VIEW - All clients with their permits */}
      {view === 'all-permits' && (
        <div className="clients-section">
          <div className="clients-header">
            <h2 className="section-title">
              <Shield size={24} />
              All Permits ({allPermits.length})
            </h2>
            <button className="btn-secondary" onClick={() => setView('overview')}>
              ← Back to Overview
            </button>
          </div>
          <div className="clients-table-container">
            <table className="clients-table permits-table">
              <thead>
                <tr>
                  <th>Client Name</th>
                  <th>Permit Type</th>
                  <th>Permit #</th>
                  <th>Expiration Date</th>
                  <th>Status</th>
                  <th>Contact</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allPermits.map((permit, idx) => (
                  <tr key={`${permit.id}-${idx}`} className={permit.status === 'expired' ? 'expired-row' : ''}>
                    <td>
                      <div className="client-name">{permit.client_name}</div>
                    </td>
                    <td>
                      <span className="permit-type-badge">
                        {getPermitTypeName(permit.permit_type, permit.custom_permit_name)}
                      </span>
                    </td>
                    <td>{permit.permit_number}</td>
                    <td>
                      <span className={`expiration-date ${permit.status === 'expired' ? 'expired' : ''}`}>
                        {formatDate(permit.expiration_date)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge-small status-${permit.status}`}>
                        {permit.status}
                      </span>
                    </td>
                    <td>
                      <div className="contact-stacked">
                        <span className="contact-item">
                          <Phone size={14} />
                          {permit.client_phone}
                        </span>
                        <span className="contact-item">
                          <Mail size={14} />
                          {permit.client_email}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon-edit"
                          onClick={() => handleViewPermits({
                            user_id: permit.user_id,
                            full_name: permit.client_name,
                            email: permit.client_email,
                            phone: permit.client_phone
                          })}
                          title="View and manage this client's permits"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="btn-icon-test"
                          onClick={() => handleTestNotifications({
                            user_id: permit.user_id,
                            full_name: permit.client_name
                          })}
                          title="Send test email and SMS to this client"
                        >
                          <Send size={16} />
                        </button>
                        <button
                          className="btn-icon-danger"
                          onClick={() => handleDeletePermit(permit.id, permit.permit_number, permit.client_name)}
                          title="Permanently delete this permit"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* EXPIRED PERMITS VIEW - Only expired permits */}
      {view === 'expired-permits' && (
        <div className="clients-section">
          <div className="clients-header">
            <h2 className="section-title expired-title">
              <Calendar size={24} />
              Expired Permits ({expiredPermits.length})
            </h2>
            <button className="btn-secondary" onClick={() => setView('overview')}>
              ← Back to Overview
            </button>
          </div>
          {expiredPermits.length === 0 ? (
            <div className="no-expired-permits">
              <Shield size={48} />
              <p>No expired permits</p>
              <p className="hint">All permits are currently valid</p>
            </div>
          ) : (
            <div className="clients-table-container">
              <table className="clients-table expired-table">
                <thead>
                  <tr>
                    <th>Client Name</th>
                    <th>Expired Permit</th>
                    <th>Permit #</th>
                    <th>Expired On</th>
                    <th>Phone</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {expiredPermits.map((permit, idx) => (
                    <tr key={`${permit.id}-${idx}`} className="expired-row">
                      <td>
                        <div className="client-name">{permit.client_name}</div>
                      </td>
                      <td>
                        <span className="permit-type-badge expired-permit-badge">
                          {getPermitTypeName(permit.permit_type, permit.custom_permit_name)}
                        </span>
                      </td>
                      <td>{permit.permit_number}</td>
                      <td>
                        <span className="expiration-date expired">
                          {formatDate(permit.expiration_date)}
                        </span>
                      </td>
                      <td>
                        <span className="contact-item">
                          <Phone size={14} />
                          {permit.client_phone}
                        </span>
                      </td>
                      <td>
                        <span className="contact-item">
                          <Mail size={14} />
                          {permit.client_email}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {view === 'clients' && (
        <div className="clients-section">
          <div className="clients-header">
            <h2 className="section-title">All Clients ({filteredClients.length})</h2>
            <div className="header-actions">
              {/* Filter Buttons */}
              <div className="client-filter-buttons">
                <button
                  className={`filter-btn ${clientFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setClientFilter('all')}
                >
                  All ({clients.length})
                </button>
                <button
                  className={`filter-btn ${clientFilter === 'individual' ? 'active' : ''}`}
                  onClick={() => setClientFilter('individual')}
                >
                  👤 Individual ({clients.filter(c => c.account_type === 'individual').length})
                </button>
                <button
                  className={`filter-btn ${clientFilter === 'company' ? 'active' : ''}`}
                  onClick={() => setClientFilter('company')}
                >
                  🏢 Company ({clients.filter(c => c.account_type === 'company').length})
                </button>
              </div>
              {selectedUsers.length > 0 && (
                <button
                  className="btn-danger delete-selected-btn"
                  onClick={handleDeleteSelected}
                  disabled={deleting}
                >
                  <Trash2 size={18} />
                  {deleting ? 'Deleting...' : `Delete Selected (${selectedUsers.length})`}
                </button>
              )}
            </div>
          </div>

          <div className="clients-table-container">
            <table className="clients-table">
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredClients.length && filteredClients.length > 0}
                      onChange={handleSelectAll}
                      className="user-checkbox"
                    />
                  </th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Permit Type</th>
                  <th>Expiration Date</th>
                  <th>Email & Phone</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr key={client.user_id} className={selectedUsers.includes(client.user_id) ? 'selected' : ''}>
                    <td className="checkbox-col">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(client.user_id)}
                        onChange={() => handleSelectUser(client.user_id)}
                        className="user-checkbox"
                      />
                    </td>
                    <td>
                      <div 
                        className="client-name clickable"
                        onClick={() => handleViewPermits(client)}
                        title="Click to view permits"
                      >
                        {client.account_type === 'company' ? (client.company_name || client.full_name) : client.full_name}
                      </div>
                      {client.account_type === 'company' && client.company_name && (
                        <div className="client-admin-name">Admin: {client.full_name}</div>
                      )}
                      <div className="client-account-type">{client.account_type === 'company' ? '🏢 Company' : '👤 Individual'}</div>
                    </td>
                    <td>
                      <div className="subscription-status-cell">
                        {client.promo_code_used ? (
                          <span className="subscription-badge subscription-promo" title={`Promo code: ${client.promo_code_used}`}>
                            🎁 PROMO ({client.promo_code_used})
                          </span>
                        ) : (
                          <>
                            <span className={`subscription-badge subscription-${client.subscription_status}`}>
                              {client.subscription_status === 'active' && '✓ PAID'}
                              {client.subscription_status === 'trial' && `🕐 TRIAL (${client.days_remaining}d)`}
                              {client.subscription_status === 'expired' && '✗ EXPIRED'}
                              {client.subscription_status === 'canceling' && '⚠ CANCELING'}
                            </span>
                            {client.subscription_status === 'expired' && (
                              <button
                                className="btn-fix-subscription"
                                onClick={() => {
                                  setFixingSubscriptionUser(client);
                                  setShowFixSubscriptionModal(true);
                                }}
                                title="Manually activate subscription after confirming payment"
                              >
                                🔧 Fix
                              </button>
                            )}
                          </>
                        )}
                        {client.subscription_status === 'expired' && client.days_until_deletion !== null && (
                          <span className="deletion-warning">
                            🗑️ Deletes in {client.days_until_deletion} days
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      {client.permits.length > 0 ? (
                        <div className="permits-type-list">
                          {client.permits.map((permit) => (
                            <span key={permit.id} className="permit-type-badge">
                              {getPermitTypeName(permit.permit_type, permit.custom_permit_name)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="no-permits">No permits</span>
                      )}
                    </td>
                    <td>
                      {client.permits.length > 0 ? (
                        <div className="expiration-list">
                          {client.permits.map((permit) => (
                            <div key={permit.id} className="expiration-item">
                              <span className={`expiration-date ${permit.status === 'expired' ? 'expired' : ''}`}>
                                {formatDate(permit.expiration_date)}
                              </span>
                              <span className={`status-badge-small status-${permit.status}`}>
                                {permit.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="no-permits">—</span>
                      )}
                    </td>
                    <td>
                      <div className="client-contact">
                        <span className="contact-item">
                          <Mail size={14} />
                          {client.email}
                        </span>
                        <span className="contact-item">
                          <Phone size={14} />
                          {client.phone}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon-edit"
                          onClick={() => handleViewPermits(client)}
                          title="View and manage this client's permits"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="btn-icon-test"
                          onClick={() => handleTestNotifications(client)}
                          title="Send a test email and SMS to verify delivery"
                        >
                          <Send size={16} />
                        </button>
                        {client.account_type === 'individual' && (
                          <button
                            className="btn-icon-move"
                            onClick={() => handleOpenMarkAsEmployee(client)}
                            title="Merge this individual into a company account"
                          >
                            <Users size={16} />
                          </button>
                        )}
                        <button
                          className="btn-icon-danger"
                          onClick={() => {
                            setSelectedUsers([client.user_id]);
                            setTimeout(() => handleDeleteSelected(), 100);
                          }}
                          title="Permanently delete this client and all their permits"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Permits View for a specific client */}
      {view === 'permits' && selectedClient && (
        <div className="permits-section">
          <div className="permits-header">
            <button 
              className="btn-back"
              onClick={() => {
                setView('clients');
                setSelectedClient(null);
                setClientPermits([]);
              }}
            >
              ← Back to Clients
            </button>
            <h2 className="section-title">
              Permits for {selectedClient.full_name}
            </h2>
          </div>

          {loadingPermits ? (
            <div className="loading-permits">Loading permits...</div>
          ) : clientPermits.length === 0 ? (
            <div className="no-permits-message">
              <FileText size={48} />
              <p>This client has no permits yet.</p>
            </div>
          ) : (
            <div className="admin-permits-grid">
              {clientPermits.map((permit) => (
                <div key={permit.id} className="admin-permit-card">
                  <div className="permit-card-header">
                    <span className="permit-type-label">
                      {getPermitTypeName(permit.permit_type, permit.custom_permit_name)}
                    </span>
                    <span className={`status-badge status-${permit.status}`}>
                      {permit.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="permit-card-body">
                    {permit.employee_name && (
                      <div className="permit-detail">
                        <span className="detail-label">Employee</span>
                        <span className="detail-value">{permit.employee_name}</span>
                      </div>
                    )}
                    <div className="permit-detail">
                      <span className="detail-label">Permit #</span>
                      <span className="detail-value mono">{permit.permit_number}</span>
                    </div>
                    <div className="permit-detail">
                      <span className="detail-label">Issue Date</span>
                      <span className="detail-value">{formatDate(permit.issue_date)}</span>
                    </div>
                    <div className="permit-detail">
                      <span className="detail-label">Expiration Date</span>
                      <span className="detail-value">{formatDate(permit.expiration_date)}</span>
                    </div>
                    {permit.next_qualification && (
                      <div className="permit-detail">
                        <span className="detail-label">Next Qualification</span>
                        <span className="detail-value">{formatDate(permit.next_qualification)}</span>
                      </div>
                    )}
                    {permit.permit_type === 'firearms' && permit.next_qual_window_start && permit.next_qual_window_end && (
                      <div className="permit-qual-window">
                        <span className="window-title-small">QUALIFICATION WINDOW (4-6 mo)</span>
                        <div className="window-dates-small">
                          <span>{formatDate(permit.next_qual_window_start)}</span>
                          <span className="window-arrow">→</span>
                          <span>{formatDate(permit.next_qual_window_end)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="permit-card-actions">
                    <button 
                      className="btn-primary btn-sm"
                      onClick={() => handleEditPermit(permit)}
                    >
                      <Edit2 size={14} />
                      Edit
                    </button>
                    <button 
                      className="btn-danger btn-sm"
                      onClick={() => handleDeletePermit(permit.id, permit.permit_number, selectedClient.full_name)}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit Permit Modal */}
      {editingPermit && (
        <EditPermitModal
          permit={editingPermit}
          onClose={() => setEditingPermit(null)}
          onSuccess={handlePermitUpdated}
        />
      )}

      {/* Overdue Qualifications View */}
      {view === 'overdue-qualifications' && (
        <div className="clients-section">
          <div className="clients-header">
            <h2 className="section-title overdue-title">
              <Calendar size={24} />
              Overdue Qualifications ({overdueQualifications.length})
            </h2>
            <div className="header-actions">
              <button 
                className="btn-sync"
                onClick={handleSyncQualifications}
                disabled={syncingQualifications}
                title="Sync completion status from permit data to fix incorrect overdue entries"
              >
                {syncingQualifications ? 'Syncing...' : '🔄 Sync & Fix Overdue'}
              </button>
              <button className="btn-secondary" onClick={() => setView('overview')}>
                ← Back to Overview
              </button>
            </div>
          </div>
          {overdueQualifications.length === 0 ? (
            <div className="no-overdue-quals">
              <Shield size={48} />
              <p>No overdue qualifications</p>
              <p className="hint">All BSIS qualifications are up to date</p>
            </div>
          ) : (
            <>
              {/* Bulk action bar */}
              {selectedQualifications.length > 0 && (
                <div className="bulk-action-bar">
                  <span>{selectedQualifications.length} selected</span>
                  <button className="btn-mark-complete" onClick={handleMarkComplete}>
                    ✓ Mark Complete
                  </button>
                  <button className="btn-delete-quals" onClick={handleDeleteQualifications}>
                    🗑 Delete Records
                  </button>
                  <button className="btn-clear-selection" onClick={() => setSelectedQualifications([])}>
                    Clear
                  </button>
                </div>
              )}
              <div className="clients-table-container">
                <table className="clients-table overdue-table">
                  <thead>
                    <tr>
                      <th>
                        <input 
                          type="checkbox"
                          checked={selectedQualifications.length === overdueQualifications.length && overdueQualifications.length > 0}
                          onChange={handleSelectAllQualifications}
                        />
                      </th>
                      <th>Client Name</th>
                      <th>Type</th>
                      <th>Permit #</th>
                      <th>Qualification</th>
                      <th>Due Date</th>
                      <th>Days Overdue</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...overdueQualifications]
                      .sort((a, b) => a.user_name.localeCompare(b.user_name))
                      .map((qual, idx) => (
                      <tr key={`${qual.qualification_id}-${idx}`} className={`overdue-row ${selectedQualifications.includes(qual.qualification_id) ? 'selected' : ''}`}>
                        <td>
                          <input 
                            type="checkbox"
                            checked={selectedQualifications.includes(qual.qualification_id)}
                            onChange={() => handleSelectQualification(qual.qualification_id)}
                          />
                        </td>
                        <td>
                          <div className="client-name">
                            {qual.user_name}
                            {qual.account_type === 'company' && qual.company_name && (
                              <div className="company-name-sub">{qual.company_name}</div>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className={`account-type-badge-small ${qual.account_type === 'company' ? 'company' : 'individual'}`}>
                            {qual.account_type === 'company' ? '🏢' : '👤'}
                          </span>
                        </td>
                        <td>
                        <span className="permit-number-badge">{qual.permit_number}</span>
                      </td>
                      <td>
                        <span className="qualification-badge">
                          {qual.qualification_name}
                        </span>
                      </td>
                      <td>
                        <span className="overdue-date">
                          {formatDate(qual.due_date)}
                        </span>
                      </td>
                      <td>
                        <span className="days-overdue-badge">
                          {qual.days_overdue} days
                        </span>
                      </td>
                      <td>
                        <span className="contact-item">
                          <Phone size={14} />
                          {qual.user_phone}
                        </span>
                      </td>
                      <td>
                        <span className="contact-item">
                          <Mail size={14} />
                          {qual.user_email}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-icon-edit"
                            onClick={() => handleViewPermits({
                              user_id: qual.user_id,
                              full_name: qual.user_name,
                              email: qual.user_email,
                              phone: qual.user_phone
                            })}
                            title="View and manage this client's permits"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            className="btn-icon-test"
                            onClick={() => handleTestNotifications({
                              user_id: qual.user_id,
                              full_name: qual.user_name
                            })}
                            title="Manually send a reminder notification"
                          >
                            <Send size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}

      {/* Individual Clients View */}
      {view === 'individual-clients' && (
        <div className="clients-section">
          <div className="clients-header">
            <h2 className="section-title">
              👤 Individual Clients ({clients.filter(c => c.account_type === 'individual').length})
            </h2>
            <div className="header-actions">
              <button
                className="btn-primary"
                onClick={() => {
                  setAddClientForm({ ...addClientForm, account_type: 'individual' });
                  setShowAddClientModal(true);
                }}
                title="Create an account for a new individual client"
              >
                <Plus size={18} />
                Add Client
              </button>
              {selectedUsers.length > 0 && (
                <button
                  className="btn-danger delete-selected-btn"
                  onClick={handleDeleteSelected}
                  disabled={deleting}
                >
                  <Trash2 size={18} />
                  {deleting ? 'Deleting...' : `Delete Selected (${selectedUsers.length})`}
                </button>
              )}
            </div>
          </div>

          <div className="clients-table-container">
            <table className="clients-table">
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredClients.length && filteredClients.length > 0}
                      onChange={handleSelectAll}
                      className="user-checkbox"
                    />
                  </th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Permits</th>
                  <th>Email & Phone</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr key={client.user_id} className={selectedUsers.includes(client.user_id) ? 'selected' : ''}>
                    <td className="checkbox-col">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(client.user_id)}
                        onChange={() => handleSelectUser(client.user_id)}
                        className="user-checkbox"
                      />
                    </td>
                    <td>
                      <div 
                        className="client-name clickable"
                        onClick={() => handleViewPermits(client)}
                        title="Click to view permits"
                      >
                        {client.full_name}
                      </div>
                    </td>
                    <td>
                      <div className="subscription-status-cell">
                        {client.promo_code_used ? (
                          <span className="subscription-badge subscription-promo" title={`Promo code: ${client.promo_code_used}`}>
                            🎁 PROMO ({client.promo_code_used})
                          </span>
                        ) : (
                          <>
                            <span className={`subscription-badge subscription-${client.subscription_status}`}>
                              {client.subscription_status === 'active' && '✓ PAID'}
                              {client.subscription_status === 'trial' && `🕐 TRIAL (${client.days_remaining}d)`}
                              {client.subscription_status === 'expired' && '✗ EXPIRED'}
                              {client.subscription_status === 'canceling' && '⚠ CANCELING'}
                            </span>
                            {client.subscription_status === 'expired' && (
                              <button
                                className="btn-fix-subscription"
                                onClick={() => {
                                  setFixingSubscriptionUser(client);
                                  setShowFixSubscriptionModal(true);
                                }}
                                title="Manually activate subscription after confirming payment"
                              >
                                🔧 Fix
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    <td>{client.permits?.length || 0} permits</td>
                    <td>
                      <div className="contact-info">
                        <span className="contact-item">
                          <Mail size={14} />
                          {client.email}
                        </span>
                        <span className="contact-item">
                          <Phone size={14} />
                          {client.phone}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleViewPermits(client)}
                          title="View all permits for this client"
                        >
                          <FileText size={16} />
                        </button>
                        <button
                          className="btn-icon btn-test"
                          onClick={() => handleTestNotifications(client)}
                          title="Send test email and SMS notifications"
                        >
                          <Send size={16} />
                        </button>
                        <button
                          className="btn-icon btn-move-to-company"
                          onClick={() => handleOpenMarkAsEmployee(client)}
                          title="Merge this individual into a company account"
                        >
                          <Users size={16} />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDeleteUser(client.user_id, client.full_name)}
                          title="Permanently delete this client account"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Company Clients View */}
      {view === 'company-clients' && (
        <div className="clients-section">
          <div className="clients-header">
            <h2 className="section-title">
              🏢 Company Clients ({clients.filter(c => c.account_type === 'company').length})
            </h2>
            <div className="header-actions">
              <button
                className="btn-primary"
                onClick={() => {
                  setAddClientForm({ ...addClientForm, account_type: 'company' });
                  setShowAddClientModal(true);
                }}
                title="Create an account for a new company"
              >
                <Plus size={18} />
                Add Company
              </button>
              {selectedUsers.length > 0 && (
                <button
                  className="btn-danger delete-selected-btn"
                  onClick={handleDeleteSelected}
                  disabled={deleting}
                >
                  <Trash2 size={18} />
                  {deleting ? 'Deleting...' : `Delete Selected (${selectedUsers.length})`}
                </button>
              )}
            </div>
          </div>

          <div className="clients-table-container">
            <table className="clients-table">
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredClients.length && filteredClients.length > 0}
                      onChange={handleSelectAll}
                      className="user-checkbox"
                    />
                  </th>
                  <th>Company Name</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Employees</th>
                  <th>Permits</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr key={client.user_id} className={selectedUsers.includes(client.user_id) ? 'selected' : ''}>
                    <td className="checkbox-col">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(client.user_id)}
                        onChange={() => handleSelectUser(client.user_id)}
                        className="user-checkbox"
                      />
                    </td>
                    <td>
                      <div 
                        className="client-name clickable"
                        onClick={() => handleViewCompanyEmployees(client)}
                        title="Click to view employees"
                      >
                        {client.company_name || client.full_name}
                      </div>
                      <div className="client-subtext">{client.full_name}</div>
                    </td>
                    <td>
                      <div className="contact-info">
                        <span className="contact-item">
                          <Mail size={14} />
                          {client.email}
                        </span>
                        <span className="contact-item">
                          <Phone size={14} />
                          {client.phone}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="subscription-status-cell">
                        {client.promo_code_used ? (
                          <span className="subscription-badge subscription-promo" title={`Promo code: ${client.promo_code_used}`}>
                            🎁 PROMO ({client.promo_code_used})
                          </span>
                        ) : (
                          <>
                            <span className={`subscription-badge subscription-${client.subscription_status}`}>
                              {client.subscription_status === 'active' && '✓ PAID'}
                              {client.subscription_status === 'trial' && `🕐 TRIAL (${client.days_remaining}d)`}
                              {client.subscription_status === 'expired' && '✗ EXPIRED'}
                              {client.subscription_status === 'canceling' && '⚠ CANCELING'}
                            </span>
                            {client.subscription_status === 'expired' && (
                              <button
                                className="btn-fix-subscription"
                                onClick={() => {
                                  setFixingSubscriptionUser(client);
                                  setShowFixSubscriptionModal(true);
                                }}
                                title="Manually activate subscription after confirming payment"
                              >
                                🔧 Fix
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      {client.employee_count || 0} / {client.employee_limit ?? 50}
                      <button
                        className="btn-edit-limit"
                        onClick={() => handleEditEmployeeLimit(client)}
                        title="Change the maximum number of employees"
                      >
                        <Edit2 size={12} />
                      </button>
                    </td>
                    <td>{client.permits?.length || 0} permits</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEditCompanyDetails(client)}
                          title="Edit company name and admin details"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleViewPermits(client)}
                          title="View all permits for this client"
                        >
                          <FileText size={16} />
                        </button>
                        <button
                          className="btn-icon btn-test"
                          onClick={() => handleTestNotifications(client)}
                          title="Send test email and SMS notifications"
                        >
                          <Send size={16} />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDeleteUser(client.user_id, client.full_name)}
                          title="Permanently delete this client account"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Company Employees View */}
      {view === 'company-employees' && selectedCompanyForView && (
        <div className="company-employees-section">
          <div className="section-header">
            <button 
              className="back-btn"
              onClick={() => { setView('company-clients'); setSelectedCompanyForView(null); setSelectedEmployeeForPermits(null); }}
            >
              ← Back to Companies
            </button>
            <h2 className="section-title">
              <Building2 size={24} />
              {selectedCompanyForView.company_name || selectedCompanyForView.full_name} - Employees
            </h2>
          </div>

          {loadingCompanyEmployees ? (
            <div className="loading-state">Loading employees...</div>
          ) : companyEmployees.length === 0 ? (
            <div className="empty-state">
              <Users size={48} />
              <h3>No employees found</h3>
              <p>This company has no permits with employee names</p>
            </div>
          ) : !selectedEmployeeForPermits ? (
            // Show employee list
            <div className="employees-grid">
              {companyEmployees.map((employee, idx) => (
                <div 
                  key={idx} 
                  className={`employee-card clickable ${employee.employee_status === 'terminated' ? 'terminated' : ''}`}
                  onClick={() => handleViewEmployeePermits(employee)}
                >
                  <div className="employee-avatar">
                    {employee.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="employee-info">
                    <h4 className="employee-name">{employee.name}</h4>
                    <p className="employee-permits">{employee.permitCount} permit{employee.permitCount !== 1 ? 's' : ''}</p>
                    {employee.employee_status === 'terminated' && (
                      <span className="status-badge-small terminated">Terminated</span>
                    )}
                  </div>
                  <div className="employee-arrow">→</div>
                </div>
              ))}
            </div>
          ) : (
            // Show selected employee's permits
            <div className="employee-permits-view">
              <button 
                className="back-btn"
                onClick={() => setSelectedEmployeeForPermits(null)}
              >
                ← Back to Employees
              </button>
              <h3 className="employee-permits-title">
                <User size={20} />
                {selectedEmployeeForPermits.name}'s Permits ({selectedEmployeeForPermits.permitCount})
              </h3>
              
              <div className="permits-list">
                {selectedEmployeeForPermits.permits.map((permit) => (
                  <div key={permit.id} className="permit-card-admin">
                    <div className="permit-header">
                      <span className={`permit-type-badge ${permit.permit_type}`}>
                        {getPermitTypeName(permit.permit_type)}
                      </span>
                      <span className={`permit-status ${permit.status}`}>
                        {permit.status}
                      </span>
                    </div>
                    <div className="permit-details">
                      <p><strong>Permit #:</strong> {permit.permit_number}</p>
                      <p><strong>Issue Date:</strong> {formatDate(permit.issue_date)}</p>
                      <p><strong>Expiration:</strong> {formatDate(permit.expiration_date)}</p>
                    </div>
                    <div className="permit-actions">
                      <button 
                        className="btn-edit-sm"
                        onClick={() => handleEditPermit(permit)}
                      >
                        <Edit2 size={14} />
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Promo Codes View */}
      {view === 'promo-codes' && (
        <div className="promo-codes-section">
          <div className="promo-codes-header">
            <h2 className="section-title">
              <Gift size={24} />
              Promo Codes
            </h2>
            <button 
              className="btn-primary"
              onClick={() => setShowPromoForm(!showPromoForm)}
            >
              <Plus size={18} />
              {showPromoForm ? 'Cancel' : 'Create New Code'}
            </button>
          </div>

          {showPromoForm && (
            <div className="promo-form-card">
              <h3>Create New Promo Code</h3>
              <form onSubmit={handleCreatePromoCode}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Promo Code</label>
                    <div className="code-input-group">
                      <input
                        type="text"
                        value={newPromoCode.code}
                        onChange={(e) => setNewPromoCode({ ...newPromoCode, code: e.target.value.toUpperCase() })}
                        placeholder="e.g., STC-FREE2024"
                        className="input-field"
                        required
                      />
                      <button type="button" className="btn-secondary btn-sm" onClick={generateRandomCode}>
                        Generate
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Valid For (Days)</label>
                    <input
                      type="number"
                      value={newPromoCode.valid_for_days}
                      onChange={(e) => setNewPromoCode({ ...newPromoCode, valid_for_days: parseInt(e.target.value) || 365 })}
                      className="input-field"
                      min="1"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Description</label>
                    <input
                      type="text"
                      value={newPromoCode.description}
                      onChange={(e) => setNewPromoCode({ ...newPromoCode, description: e.target.value })}
                      placeholder="Free account promo code"
                      className="input-field"
                    />
                  </div>
                  <div className="form-group">
                    <label>Max Uses (leave empty for unlimited)</label>
                    <input
                      type="number"
                      value={newPromoCode.max_uses || ''}
                      onChange={(e) => setNewPromoCode({ ...newPromoCode, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                      placeholder="Unlimited"
                      className="input-field"
                      min="1"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Valid For Account Types</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newPromoCode.account_types.includes('individual')}
                        onChange={(e) => {
                          const types = e.target.checked 
                            ? [...newPromoCode.account_types, 'individual']
                            : newPromoCode.account_types.filter(t => t !== 'individual');
                          setNewPromoCode({ ...newPromoCode, account_types: types });
                        }}
                      />
                      Individual
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newPromoCode.account_types.includes('company')}
                        onChange={(e) => {
                          const types = e.target.checked 
                            ? [...newPromoCode.account_types, 'company']
                            : newPromoCode.account_types.filter(t => t !== 'company');
                          setNewPromoCode({ ...newPromoCode, account_types: types });
                        }}
                      />
                      Company
                    </label>
                  </div>
                </div>
                <button type="submit" className="btn-primary">
                  Create Promo Code
                </button>
              </form>
            </div>
          )}

          <div className="promo-codes-list">
            {promoCodes.length === 0 ? (
              <div className="no-promo-codes">
                <Gift size={48} />
                <p>No promo codes yet</p>
                <p className="hint">Create a promo code to give free access to clients</p>
              </div>
            ) : (
              <table className="promo-codes-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Description</th>
                    <th>Valid For</th>
                    <th>Usage</th>
                    <th>Account Types</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {promoCodes.map((promo) => (
                    <tr key={promo.id} className={!promo.is_active ? 'inactive' : ''}>
                      <td>
                        <div className="promo-code-cell">
                          <span className="promo-code-text">{promo.code}</span>
                          <button 
                            className="btn-icon-copy"
                            onClick={() => copyPromoCode(promo.code)}
                            title="Copy code"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      </td>
                      <td>{promo.description}</td>
                      <td>{promo.valid_for_days} days</td>
                      <td>
                        {promo.times_used || 0}
                        {promo.max_uses ? ` / ${promo.max_uses}` : ' / ∞'}
                      </td>
                      <td>
                        <div className="account-types-badges">
                          {promo.account_types?.includes('individual') && (
                            <span className="type-badge individual">Individual</span>
                          )}
                          {promo.account_types?.includes('company') && (
                            <span className="type-badge company">Company</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${promo.is_active ? 'status-active' : 'status-expired'}`}>
                          {promo.is_active ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className={`btn-icon-toggle ${promo.is_active ? 'enabled' : 'disabled'}`}
                            onClick={() => handleTogglePromoCode(promo.code)}
                            title={promo.is_active ? 'Disable code' : 'Enable code'}
                          >
                            {promo.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                          </button>
                          <button
                            className="btn-icon-danger"
                            onClick={() => handleDeletePromoCode(promo.code)}
                            title="Delete code"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Activity Log View */}
      {view === 'activity' && (
        <div className="activity-section">
          <div className="section-header">
            <h2 className="section-title">
              <Activity size={24} />
              Activity Log
            </h2>
            <button className="btn-refresh" onClick={fetchActivityLog}>
              Refresh
            </button>
          </div>
          
          {loadingActivity ? (
            <div className="loading-state">Loading activity...</div>
          ) : activityLog.length === 0 ? (
            <div className="empty-state">
              <Activity size={48} />
              <h3>No activity yet</h3>
              <p>Activity will appear here as users make changes</p>
            </div>
          ) : (
            <div className="activity-list">
              {activityLog.map((activity, idx) => (
                <div key={activity.id || idx} className="activity-item">
                  <div className="activity-icon">
                    {activity.action_type.includes('permit') && <FileText size={20} />}
                    {activity.action_type.includes('employee') && <User size={20} />}
                    {activity.action_type.includes('qualification') && <Calendar size={20} />}
                    {!activity.action_type.includes('permit') && 
                     !activity.action_type.includes('employee') && 
                     !activity.action_type.includes('qualification') && <Activity size={20} />}
                  </div>
                  <div className="activity-content">
                    <div className="activity-header">
                      <span className="activity-user">
                        {activity.account_type === 'company' ? <Building2 size={14} /> : <User size={14} />}
                        {activity.user_name}
                      </span>
                      <span className={`activity-type-badge ${activity.action_type.includes('created') ? 'created' : activity.action_type.includes('updated') ? 'updated' : activity.action_type.includes('terminated') ? 'terminated' : 'other'}`}>
                        {activity.action_type.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <p className="activity-description">{activity.action_description}</p>
                    <div className="activity-meta">
                      <span className="activity-email">{activity.user_email}</span>
                      <span className="activity-time">
                        <Clock size={12} />
                        {new Date(activity.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Expired Trials View */}
      {view === 'expired-trials' && (
        <div className="expired-trials-section">
          <div className="section-header">
            <h2 className="section-title">
              <Clock size={24} />
              Expired Trial Users
            </h2>
            <p className="section-subtitle">
              Users who started a trial but didn't subscribe. Reach out with offers!
            </p>
          </div>
          
          {(() => {
            const expiredTrialUsers = clients.filter(c => 
              c.subscription_status === 'expired' && 
              !c.promo_code_used &&
              !c.stripe_subscription_id // Never had a paid subscription
            ).sort((a, b) => {
              // Sort by most recently expired first
              const dateA = a.trial_end_date ? new Date(a.trial_end_date) : new Date(0);
              const dateB = b.trial_end_date ? new Date(b.trial_end_date) : new Date(0);
              return dateB - dateA;
            });

            if (expiredTrialUsers.length === 0) {
              return (
                <div className="empty-state">
                  <Clock size={48} />
                  <h3>No expired trial users</h3>
                  <p>All trial users have either subscribed or their accounts have been deleted</p>
                </div>
              );
            }

            return (
              <div className="expired-trials-content">
                <div className="expired-trials-summary">
                  <div className="summary-stat">
                    <span className="stat-number">{expiredTrialUsers.length}</span>
                    <span className="stat-label">Total Expired Trials</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-number">{expiredTrialUsers.filter(u => u.account_type === 'individual').length}</span>
                    <span className="stat-label">Individuals</span>
                  </div>
                  <div className="summary-stat">
                    <span className="stat-number">{expiredTrialUsers.filter(u => u.account_type === 'company').length}</span>
                    <span className="stat-label">Companies</span>
                  </div>
                </div>

                <table className="admin-table expired-trials-table">
                  <thead>
                    <tr>
                      <th>NAME</th>
                      <th>TYPE</th>
                      <th>EMAIL</th>
                      <th>PHONE</th>
                      <th>TRIAL EXPIRED</th>
                      <th>DAYS SINCE</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expiredTrialUsers.map((user) => {
                      const expiredDate = user.trial_end_date ? new Date(user.trial_end_date) : null;
                      const daysSinceExpiry = expiredDate ? Math.floor((new Date() - expiredDate) / (1000 * 60 * 60 * 24)) : null;
                      
                      return (
                        <tr key={user.user_id} className="expired-trial-row">
                          <td>
                            <div className="user-name-cell">
                              <span className="user-name">{user.full_name}</span>
                              {user.account_type === 'company' && user.company_name && (
                                <span className="company-name-sub">{user.company_name}</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className={`type-badge ${user.account_type}`}>
                              {user.account_type === 'company' ? '🏢 Company' : '👤 Individual'}
                            </span>
                          </td>
                          <td>
                            <a href={`mailto:${user.email}`} className="email-link">
                              <Mail size={14} />
                              {user.email}
                            </a>
                          </td>
                          <td>
                            <a href={`tel:${user.phone}`} className="phone-link">
                              <Phone size={14} />
                              {user.phone}
                            </a>
                          </td>
                          <td>
                            <span className="expired-date">
                              {expiredDate ? expiredDate.toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              }) : 'Unknown'}
                            </span>
                          </td>
                          <td>
                            <span className={`days-badge ${daysSinceExpiry > 14 ? 'urgent' : daysSinceExpiry > 7 ? 'warning' : 'recent'}`}>
                              {daysSinceExpiry !== null ? `${daysSinceExpiry} days ago` : 'N/A'}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-icon-action"
                                onClick={() => window.open(`mailto:${user.email}?subject=Special Offer from Southwest Training Center&body=Hi ${user.full_name},%0D%0A%0D%0AWe noticed you recently tried our Permit Tracker. We'd love to have you back!%0D%0A%0D%0AContact us for a special offer.%0D%0A%0D%0ABest regards,%0D%0ASouthwest Training Center`, '_blank')}
                                title="Send Email"
                              >
                                <Mail size={16} />
                              </button>
                              <button
                                className="btn-icon-action"
                                onClick={() => window.open(`tel:${user.phone}`, '_blank')}
                                title="Call"
                              >
                                <Phone size={16} />
                              </button>
                              <button
                                className="btn-icon-action btn-fix"
                                onClick={() => {
                                  setFixingSubscriptionUser(user);
                                  setShowFixSubscriptionModal(true);
                                }}
                                title="Manually activate subscription"
                              >
                                🔧
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {/* Duplicate Permits View */}
      {view === 'duplicate-permits' && (
        <div className="duplicate-permits-section">
          <div className="section-header">
            <h2 className="section-title">
              <Copy size={24} />
              Duplicate Permits
            </h2>
            <p className="section-subtitle">
              Permits with the same number appearing under different accounts. Review and merge to clean up data.
            </p>
            <button className="btn-refresh" onClick={fetchDuplicatePermits}>
              Refresh
            </button>
          </div>

          {loadingDuplicates ? (
            <div className="loading-state">Loading duplicate permits...</div>
          ) : duplicatePermits.length === 0 ? (
            <div className="empty-state">
              <Copy size={48} />
              <h3>No duplicate permits found</h3>
              <p>All permit numbers are unique across accounts</p>
            </div>
          ) : (
            <div className="duplicates-list">
              <div className="duplicates-summary">
                <span className="summary-badge">{duplicatePermits.length} duplicate permit numbers found</span>
              </div>
              
              {duplicatePermits.map((dup) => (
                <div key={dup.permit_number} className="duplicate-card">
                  <div className="duplicate-header" onClick={() => setSelectedDuplicate(selectedDuplicate === dup.permit_number ? null : dup.permit_number)}>
                    <div className="permit-info">
                      <span className="permit-number-badge">#{dup.permit_number}</span>
                      <span className="count-badge">{dup.count} copies</span>
                    </div>
                    <button className="expand-btn">
                      {selectedDuplicate === dup.permit_number ? '▲' : '▼'}
                    </button>
                  </div>
                  
                  {selectedDuplicate === dup.permit_number && (
                    <div className="duplicate-details">
                      <div className="permits-comparison">
                        {dup.permits.map((permit, idx) => (
                          <div key={permit.permit_id} className={`permit-column ${permit.account_type}`}>
                            <div className="column-header">
                              <span className={`type-badge ${permit.account_type}`}>
                                {permit.account_type === 'company' ? '🏢 COMPANY' : '👤 INDIVIDUAL'}
                              </span>
                              {idx === 0 && <span className="keep-badge">KEEP THIS</span>}
                              {idx > 0 && <span className="delete-badge">DELETE THIS</span>}
                            </div>
                            
                            <div className="permit-details">
                              <div className="detail-row">
                                <span className="label">Account:</span>
                                <span className="value">{permit.user_name}</span>
                              </div>
                              {permit.company_name && (
                                <div className="detail-row">
                                  <span className="label">Company:</span>
                                  <span className="value">{permit.company_name}</span>
                                </div>
                              )}
                              {permit.employee_name && (
                                <div className="detail-row">
                                  <span className="label">Employee:</span>
                                  <span className="value">{permit.employee_name}</span>
                                </div>
                              )}
                              <div className="detail-row">
                                <span className="label">Email:</span>
                                <span className="value email">{permit.user_email}</span>
                              </div>
                              <div className="detail-row">
                                <span className="label">Issue Date:</span>
                                <span className="value">{permit.issue_date ? new Date(permit.issue_date).toLocaleDateString() : 'N/A'}</span>
                              </div>
                              <div className="detail-row">
                                <span className="label">Expiration:</span>
                                <span className="value">{permit.expiration_date ? new Date(permit.expiration_date).toLocaleDateString() : 'N/A'}</span>
                              </div>
                              
                              <div className="qual-section">
                                <span className="qual-title">Qualifications:</span>
                                <div className="qual-row">
                                  <span>Q1:</span>
                                  <span className={permit.qual_1?.completed ? 'completed' : 'pending'}>
                                    {permit.qual_1?.date ? new Date(permit.qual_1.date).toLocaleDateString() : 'Not set'}
                                    {permit.qual_1?.completed && ' ✓'}
                                  </span>
                                </div>
                                <div className="qual-row">
                                  <span>Q2:</span>
                                  <span className={permit.qual_2?.completed ? 'completed' : 'pending'}>
                                    {permit.qual_2?.date ? new Date(permit.qual_2.date).toLocaleDateString() : 'Not set'}
                                    {permit.qual_2?.completed && ' ✓'}
                                  </span>
                                </div>
                                <div className="qual-row">
                                  <span>Q3:</span>
                                  <span className={permit.qual_3?.completed ? 'completed' : 'pending'}>
                                    {permit.qual_3?.date ? new Date(permit.qual_3.date).toLocaleDateString() : 'Not set'}
                                    {permit.qual_3?.completed && ' ✓'}
                                  </span>
                                </div>
                                <div className="qual-row">
                                  <span>Q4:</span>
                                  <span className={permit.qual_4_renewal?.completed ? 'completed' : 'pending'}>
                                    {permit.qual_4_renewal?.date ? new Date(permit.qual_4_renewal.date).toLocaleDateString() : 'Not set'}
                                    {permit.qual_4_renewal?.completed && ' ✓'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {idx > 0 && (
                              <button
                                className="btn-merge"
                                onClick={() => handleMergePermits(permit.permit_id, dup.permits[0].permit_id)}
                                disabled={mergingPermit}
                              >
                                {mergingPermit ? 'Merging...' : `Delete & Merge into ${dup.permits[0].account_type === 'company' ? 'Company' : 'Individual'}`}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Archived Accounts View */}
      {view === 'archived-accounts' && (
        <div className="archived-accounts-section">
          <div className="section-header">
            <h2 className="section-title">
              <Trash2 size={24} />
              Archived Accounts
            </h2>
            <p className="section-subtitle">
              Accounts deleted after trial expiration. You can restore them if needed.
            </p>
            <button className="btn-refresh" onClick={fetchArchivedAccounts}>
              Refresh
            </button>
          </div>

          {loadingArchived ? (
            <div className="loading-state">Loading archived accounts...</div>
          ) : archivedAccounts.length === 0 ? (
            <div className="empty-state">
              <Trash2 size={48} />
              <h3>No archived accounts</h3>
              <p>Expired trial accounts will appear here after the 30-day grace period</p>
            </div>
          ) : (
            <div className="archived-content">
              <div className="archived-summary">
                <span className="summary-badge">{archivedAccounts.length} archived accounts</span>
              </div>
              
              <table className="admin-table archived-table">
                <thead>
                  <tr>
                    <th>NAME</th>
                    <th>TYPE</th>
                    <th>EMAIL</th>
                    <th>PHONE</th>
                    <th>TRIAL ENDED</th>
                    <th>ARCHIVED</th>
                    <th>PERMITS</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {archivedAccounts.map((account) => (
                    <tr key={account.archive_id}>
                      <td>
                        <div className="user-name-cell">
                          <span className="user-name">{account.full_name}</span>
                          {account.company_name && (
                            <span className="company-name-sub">{account.company_name}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`type-badge ${account.account_type}`}>
                          {account.account_type === 'company' ? '🏢 Company' : '👤 Individual'}
                        </span>
                      </td>
                      <td>{account.email}</td>
                      <td>{account.phone}</td>
                      <td>
                        {account.trial_end_date ? new Date(account.trial_end_date).toLocaleDateString() : 'Unknown'}
                      </td>
                      <td>
                        {account.archived_at ? new Date(account.archived_at).toLocaleDateString() : 'Unknown'}
                      </td>
                      <td>{account.permit_count} permits</td>
                      <td>
                        <button
                          className="btn-restore"
                          onClick={() => handleRestoreAccount(account.archive_id)}
                        >
                          Restore
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Account Settings Modal */}
      <AccountSettings
        isOpen={showAccountSettings}
        onClose={() => setShowAccountSettings(false)}
        user={adminUser}
      />

      {/* Employee Limit Edit Modal */}
      {showEmployeeLimitModal && editingCompany && (
        <div className="modal-overlay" onClick={() => setShowEmployeeLimitModal(false)}>
          <div className="modal employee-limit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Employee Limit</h2>
              <button className="close-btn" onClick={() => setShowEmployeeLimitModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-info">
                Update the maximum number of employees for <strong>{editingCompany.company_name || editingCompany.full_name}</strong>
              </p>
              
              <div className="form-group">
                <label>Company</label>
                <input type="text" value={editingCompany.email} disabled />
              </div>
              
              <div className="form-group">
                <label>Current Employees</label>
                <input type="text" value={`${editingCompany.employee_count || 0} employees`} disabled />
              </div>
              
              <div className="form-group">
                <label>Employee Limit *</label>
                <input 
                  type="number" 
                  min="1"
                  max="10000"
                  value={newEmployeeLimit}
                  onChange={(e) => setNewEmployeeLimit(e.target.value)}
                />
                <div className="limit-presets">
                  <button type="button" onClick={() => setNewEmployeeLimit(50)}>50</button>
                  <button type="button" onClick={() => setNewEmployeeLimit(100)}>100</button>
                  <button type="button" onClick={() => setNewEmployeeLimit(250)}>250</button>
                  <button type="button" onClick={() => setNewEmployeeLimit(500)}>500</button>
                  <button type="button" onClick={() => setNewEmployeeLimit(1000)}>1000</button>
                </div>
              </div>
              
              <div className="form-actions">
                <button className="btn-secondary" onClick={() => setShowEmployeeLimitModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleUpdateEmployeeLimit}>Update Limit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Company Details Modal */}
      {showEditCompanyModal && editingCompanyDetails && (
        <div className="modal-overlay" onClick={() => setShowEditCompanyModal(false)}>
          <div className="modal edit-company-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Company Details</h2>
              <button className="close-btn" onClick={() => setShowEditCompanyModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-info">
                Update company information for <strong>{editingCompanyDetails.email}</strong>
              </p>
              
              <div className="form-group">
                <label>Company Name *</label>
                <input 
                  type="text" 
                  value={editCompanyName}
                  onChange={(e) => setEditCompanyName(e.target.value)}
                  placeholder="Enter company name"
                />
              </div>
              
              <div className="form-group">
                <label>Admin/Owner Name *</label>
                <input 
                  type="text" 
                  value={editAdminName}
                  onChange={(e) => setEditAdminName(e.target.value)}
                  placeholder="Enter admin name"
                />
              </div>
              
              <div className="form-group">
                <label>Email (cannot be changed)</label>
                <input type="text" value={editingCompanyDetails.email} disabled />
              </div>
              
              <div className="form-actions">
                <button className="btn-secondary" onClick={() => setShowEditCompanyModal(false)}>Cancel</button>
                <button 
                  className="btn-primary" 
                  onClick={handleUpdateCompanyDetails}
                  disabled={!editCompanyName.trim() || !editAdminName.trim()}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Employee Modal */}
      {showMarkEmployeeModal && markingAsEmployee && (
        <div className="modal-overlay" onClick={() => setShowMarkEmployeeModal(false)}>
          <div className="modal mark-employee-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Move to Company</h2>
              <button className="close-btn" onClick={() => setShowMarkEmployeeModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-info">
                Move <strong>{markingAsEmployee.full_name}</strong> to a company account.
                They will no longer appear in the main client list.
              </p>
              
              <div className="form-group">
                <label>Client Email</label>
                <input type="text" value={markingAsEmployee.email} disabled />
              </div>
              
              <div className="form-group">
                <label>Client Permits</label>
                <input type="text" value={`${markingAsEmployee.permits?.length || 0} permits`} disabled />
              </div>
              
              <div className="form-group">
                <label>Select Company *</label>
                <select
                  value={selectedCompanyForEmployee}
                  onChange={(e) => setSelectedCompanyForEmployee(e.target.value)}
                >
                  <option value="">-- Select a Company --</option>
                  {companyAccounts.map(company => (
                    <option key={company.user_id} value={company.user_id}>
                      {company.company_name || company.full_name} ({company.email})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="merge-options">
                <div className="merge-option">
                  <h4>Option 1: Add as Employee</h4>
                  <p>Keep this account and mark as company employee. Their permits stay with them.</p>
                  <button 
                    className="btn-primary" 
                    onClick={handleMarkAsEmployee}
                    disabled={!selectedCompanyForEmployee}
                  >
                    Add as Employee
                  </button>
                </div>
                
                <div className="merge-option owner-option">
                  <h4>Option 2: Merge as Company Owner</h4>
                  <p>If this person IS the company owner, merge their permits into the company account and delete this individual account.</p>
                  <button 
                    className="btn-warning" 
                    onClick={handleMergeIntoCompany}
                    disabled={!selectedCompanyForEmployee}
                  >
                    Merge &amp; Delete Individual Account
                  </button>
                </div>
              </div>
              
              <div className="form-actions">
                <button className="btn-secondary" onClick={() => setShowMarkEmployeeModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Client Modal */}
      {showAddClientModal && (
        <div className="modal-overlay" onClick={() => setShowAddClientModal(false)}>
          <div className="modal add-client-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Plus size={20} />
                {addClientForm.account_type === 'company' ? 'Add Company Client' : 'Add Individual Client'}
              </h2>
              <button className="close-btn" onClick={() => setShowAddClientModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-info">
                Create an account for a client. They will receive their login credentials via email.
              </p>
              
              <form onSubmit={handleAddClient}>
                <div className="form-group">
                  <label>Account Type</label>
                  <select
                    value={addClientForm.account_type}
                    onChange={(e) => setAddClientForm({...addClientForm, account_type: e.target.value})}
                  >
                    <option value="individual">Individual</option>
                    <option value="company">Company</option>
                  </select>
                </div>

                {addClientForm.account_type === 'company' && (
                  <div className="form-group">
                    <label>Company Name *</label>
                    <input
                      type="text"
                      value={addClientForm.company_name}
                      onChange={(e) => setAddClientForm({...addClientForm, company_name: e.target.value})}
                      placeholder="Enter company name"
                      required={addClientForm.account_type === 'company'}
                    />
                  </div>
                )}
                
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    value={addClientForm.full_name}
                    onChange={(e) => setAddClientForm({...addClientForm, full_name: e.target.value})}
                    placeholder="Enter client's full name"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    value={addClientForm.email}
                    onChange={(e) => setAddClientForm({...addClientForm, email: e.target.value})}
                    placeholder="client@example.com"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    value={addClientForm.phone}
                    onChange={(e) => setAddClientForm({...addClientForm, phone: e.target.value})}
                    placeholder="+1-555-123-4567"
                    required
                  />
                </div>
                
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={addClientForm.send_welcome_email}
                      onChange={(e) => setAddClientForm({...addClientForm, send_welcome_email: e.target.checked})}
                    />
                    <span>Send welcome email with login credentials</span>
                  </label>
                  <p className="form-hint">
                    A random password will be generated. The client can change it after logging in.
                  </p>
                </div>
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn-secondary" 
                    onClick={() => setShowAddClientModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn-primary"
                    disabled={addingClient}
                  >
                    {addingClient ? 'Creating...' : 'Create Account'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Fix Subscription Modal */}
      {showFixSubscriptionModal && fixingSubscriptionUser && (
        <div className="modal-overlay" onClick={() => setShowFixSubscriptionModal(false)}>
          <div className="modal fix-subscription-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔧 Fix Subscription</h2>
              <button className="close-btn" onClick={() => setShowFixSubscriptionModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-info">
                Activate subscription for <strong>{fixingSubscriptionUser.full_name}</strong> ({fixingSubscriptionUser.email})?
              </p>
              <p className="modal-detail">
                This will set the subscription to <strong>ACTIVE</strong> for 1 year. 
                Use this after confirming payment was received via Stripe dashboard or other means.
              </p>
              
              <div className="form-actions">
                <button 
                  className="btn-secondary" 
                  onClick={() => setShowFixSubscriptionModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleFixSubscription}
                  disabled={fixingSubscription}
                >
                  {fixingSubscription ? 'Activating...' : 'Activate Subscription (1 Year)'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
